import React, {useState} from 'react';
import styled from 'styled-components';
import SGBody from 'components/Typography/SGBody';
import ModalIconTitle from 'features/POSRegisters/components/ModalContent/components/ModalTitle';
import ModalMainContentContainer from './components/ModalContentContainer';
import DataRow, {DataColumn} from 'features/POSRegisters/POSDiagram/components/DataGrid/DataRow';
import ModalTabs, {ModalTab} from './components/ModalTabs';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
import ScanDetails, {CfdScanDetails} from 'features/POSRegisters/interfaces/ScanDetails';
import ModalSubHeader from './components/ModalSubHeader';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import SeverityEnum, {SeverityPriorityEnum} from 'features/POSRegisters/interfaces/Severity';
import getSubTypeAlerts from 'features/POSRegisters/functions/getSubTypeAlerts';
import {AlertScanSubType} from 'features/POSRegisters/interfaces/AlertInfo';
import TabPanel from 'components/Tabs/TabPanel';
import {AdaptedDeviceData} from 'features/POSRegisters/interfaces/AdaptedDeviceData';

//#region Styled Components
const StyledModalContent = styled.div``;

//#endregion Styled Components

//#region Column Definitions
const connectivityColumns: DataColumn[] = [
  {
    id: 'pingStatus',
    label: 'PING STATUS',
    width: '6rem',
    format: ({pingStatus}) => <SGBody variant={'b4'}>{pingStatus ?? 'Failure'}</SGBody>,
  },
  {
    id: 'ipAddress',
    label: 'IP Address',
    width: 'auto',
    minWidth: '130px',
  },
  {
    id: 'macAddress',
    label: 'MAC Address',
    width: 'auto',
    minWidth: '140px',
  },
  {
    id: 'vlanNumber',
    label: 'VLAN',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'switchId',
    label: 'Switch ID',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'switchNumber',
    label: 'Switch Number',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'portNumber',
    label: 'Port Number',
    width: 'auto',
    minWidth: '100px',
  },
];

const connectivityCheckInColumns: DataColumn[] = [
  {
    id: 'mobileApiLastSeen',
    label: 'Mobile Api Last Seen',
    width: 'auto',
    minWidth: '220px',
  },
  {
    id: 'consumerFacingDisplayFirstSeenDate',
    label: 'CFD First Seen Date',
    width: 'auto',
    minWidth: '220px',
  },
  {
    id: 'consumerFacingDisplayLastSeenDate',
    label: 'CFD Last Seen Date',
    width: 'auto',
    minWidth: '220px',
  },
];

const informationNposColumns: DataColumn[] = [
  {
    id: 'consumerFacingDisplayAppVersion',
    label: 'Version',
    width: 'auto',
    minWidth: '220px',
  },
];

const informationOsColumns: DataColumn[] = [
  {
    id: 'iosVersion',
    label: 'iOS Version',
    width: 'auto',
    minWidth: '180px',
  },
];

const informationHardwareColumns: DataColumn[] = [
  {
    id: 'deviceName',
    label: 'DEVICE NAME',
    width: 'auto',
    minWidth: '120px',
  },
  {
    id: 'manufacturer',
    label: 'Manufacturer',
    width: 'auto',
    minWidth: '90px',
  },
  {
    id: 'model',
    label: 'Model',
    width: 'auto',
    minWidth: '240px',
  },
  {
    id: 'macAddress',
    label: 'Primary Mac Address',
    width: 'auto',
    minWidth: '130px',
  },
  {
    id: 'serialNumber',
    label: 'Serial Number',
    width: 'auto',
    minWidth: '130px',
  },
  {
    id: 'ipAddress',
    label: 'IP Address',
    width: 'auto',
    minWidth: '130px',
  },
];

//#endregion Column Definitions

//#region Component
interface ModalContentProps {
  deviceType: DeviceType;
  adaptedDeviceData: AdaptedDeviceData<CfdScanDetails> | null;
}

export default function IPadMiniModalContent({deviceType, adaptedDeviceData}: ModalContentProps) {
  const [activeTab, setActiveTab] = useState<ModalTab>(ModalTab.CONNECTIVITY);

  if (!adaptedDeviceData) {
    return <div>No device data available</div>;
  }
  const ipadMini = adaptedDeviceData?.ipadMini;

  const scanDetails = ipadMini?.scanResults as ScanDetails;
  const alerts = ipadMini?.alerts;

  const severity = getPriorityAlert([ipadMini]);
  const hasIssues = Number(SeverityPriorityEnum[severity.toUpperCase() as any]) > 1;

  const ipadTabs: {tab: ModalTab; severity?: SeverityEnum; isValid?: boolean}[] = [
    {
      tab: ModalTab.CONNECTIVITY,
      severity: getPriorityAlert([
        {
          scanResults: ipadMini.scanResults,
          alerts: getSubTypeAlerts(alerts, [
            AlertScanSubType.CONNECTIVITY,
            AlertScanSubType.CHECK_IN_STATUS,
          ]),
        },
      ]),
      isValid: scanDetails !== undefined,
    },
    {
      tab: ModalTab.INFORMATION,
      severity: getPriorityAlert([
        {
          scanResults: ipadMini.scanResults,
          alerts: getSubTypeAlerts(alerts, [
            AlertScanSubType.NPOS,
            AlertScanSubType.OPERATING_SYSTEM,
            AlertScanSubType.HARDWARE,
          ]),
        },
      ]),
      isValid: scanDetails !== undefined,
    },
  ];

  const handleTabChange = (tab: ModalTab) => {
    setActiveTab(tab);
  };

  return (
    <StyledModalContent>
      <ModalIconTitle
        deviceType={deviceType}
        severity={hasIssues ? severity : undefined}
        deviceName={adaptedDeviceData?.id}
      >
        <ModalTabs
          handleTabChange={handleTabChange}
          activeTabs={ipadTabs}
          selectedTab={activeTab}
        />
      </ModalIconTitle>

      <ModalMainContentContainer>
        <TabPanel value={activeTab} index={ModalTab.CONNECTIVITY}>
          <ModalSubHeader>CONNECTIVITY</ModalSubHeader>
          <DataRow
            columns={connectivityColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.CONNECTIVITY)}
          />
          <ModalSubHeader>CHECK-IN STATUS</ModalSubHeader>
          <DataRow
            columns={connectivityCheckInColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.CHECK_IN_STATUS)}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={ModalTab.INFORMATION}>
          <ModalSubHeader>NPOS</ModalSubHeader>
          <DataRow
            columns={informationNposColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.NPOS)}
          />
          <ModalSubHeader>OPERATING SYSTEM</ModalSubHeader>
          <DataRow
            columns={informationOsColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.OPERATING_SYSTEM)}
          />
          <ModalSubHeader>HARDWARE</ModalSubHeader>
          <DataRow
            columns={informationHardwareColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.HARDWARE)}
          />
        </TabPanel>
      </ModalMainContentContainer>
    </StyledModalContent>
  );
}

//#endregion Component
